// Agrupación de teléfonos por marca + modelo: una tarjeta o fila por modelo con sus variantes de
// capacidad (RAM + almacenamiento) y color. La venta sigue eligiendo un SKU. Funciones puras.
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { isService, isSoldOut, stockCount } from '@/domains/sales/wizard/catalogAvailability';
import {
    capacityGb,
    formatStorage,
    ramLabel,
    variantCapacityLabel,
} from '@/shared/lib/format/capacity';
import { formatMXN } from '@/shared/lib/format/money';

export interface ModelGroup<T extends CatalogItem = CatalogItem> {
    key: string;
    brand: string;
    model: string;
    /** De menor a mayor capacidad y, dentro de una capacidad, por color. */
    variants: T[];
    stockTotal: number;
    minPriceCents: number;
    maxPriceCents: number;
    /** La más barata con stock; sin stock en ninguna, la más barata. */
    defaultVariant: T;
    soldOut: boolean;
}

export type CatalogEntry<T extends CatalogItem = CatalogItem> =
    | { kind: 'model'; key: string; group: ModelGroup<T> }
    | { kind: 'item'; key: string; item: T };

const byText = (a: string, b: string): number => a.localeCompare(b, 'es-MX');

/** Solo un teléfono con modelo se agrupa; kits, accesorios, SIM, módems y servicios van por SKU. */
export function isGroupablePhone(item: CatalogItem): boolean {
    return (item.category ?? 'PHONE') === 'PHONE' && !isService(item) && item.model.trim() !== '';
}

/** Marca + modelo; un teléfono sin modelo no se junta con nadie y queda por su SKU. */
export function modelKeyOf(item: Pick<CatalogItem, 'brand' | 'model' | 'sku'>): string {
    if (item.model.trim() === '') return `sku:${item.sku}`;
    return `${item.brand.trim().toLowerCase()}|${item.model.trim().toLowerCase()}`;
}

/** Normaliza el valor para que «256» y «256 GB» caigan en la misma capacidad. */
function sizeKey(value: string | null | undefined): string {
    const gb = capacityGb(value);
    return gb === null ? (value?.trim().toLowerCase() ?? '') : String(gb);
}

/** Sin cifra va al final; entre cifras, primero la RAM y luego el almacenamiento. */
function compareSize(a: string | null | undefined, b: string | null | undefined): number {
    const gbA = capacityGb(a) ?? Number.POSITIVE_INFINITY;
    const gbB = capacityGb(b) ?? Number.POSITIVE_INFINITY;
    if (gbA !== gbB) return gbA < gbB ? -1 : 1;
    return byText(a?.trim() ?? '', b?.trim() ?? '');
}

function compareVariants(a: CatalogItem, b: CatalogItem): number {
    return (
        compareSize(a.ram, b.ram) ||
        compareSize(a.capacity, b.capacity) ||
        byText(a.color.trim(), b.color.trim()) ||
        byText(a.sku, b.sku)
    );
}

function cheapest<T extends CatalogItem>(items: readonly T[]): T {
    return items.reduce((best, item) => (item.cashPriceCents < best.cashPriceCents ? item : best));
}

function buildGroup<T extends CatalogItem>(
    key: string,
    items: T[],
    compare: (a: CatalogItem, b: CatalogItem) => number = compareVariants,
): ModelGroup<T> {
    const variants = [...items].sort(compare);
    const inStock = variants.filter((variant) => !isSoldOut(variant));
    const prices = variants.map((variant) => variant.cashPriceCents);
    return {
        key,
        brand: variants[0].brand,
        model: variants[0].model,
        variants,
        stockTotal: variants.reduce((sum, variant) => sum + stockCount(variant), 0),
        minPriceCents: Math.min(...prices),
        maxPriceCents: Math.max(...prices),
        defaultVariant: cheapest(inStock.length > 0 ? inStock : variants),
        soldOut: inStock.length === 0,
    };
}

/** Un grupo por marca + modelo, en el orden en que aparece el primer SKU de cada uno. */
export function groupByModel<T extends CatalogItem>(items: readonly T[]): ModelGroup<T>[] {
    const buckets = new Map<string, T[]>();
    for (const item of items) {
        const key = modelKeyOf(item);
        const bucket = buckets.get(key);
        if (bucket) bucket.push(item);
        else buckets.set(key, [item]);
    }
    return [...buckets].map(([key, bucket]) => buildGroup(key, bucket));
}

/** Teléfonos agrupados por modelo (en la posición de su primer SKU) y lo demás por SKU. */
export function catalogEntries<T extends CatalogItem>(items: readonly T[]): CatalogEntry<T>[] {
    const groups = new Map(
        groupByModel(items.filter(isGroupablePhone)).map((group) => [group.key, group]),
    );
    const entries: CatalogEntry<T>[] = [];
    const placed = new Set<string>();
    for (const item of items) {
        if (!isGroupablePhone(item)) {
            entries.push({ kind: 'item', key: item.sku, item });
            continue;
        }
        const key = modelKeyOf(item);
        const group = groups.get(key);
        if (group === undefined || placed.has(key)) continue;
        placed.add(key);
        entries.push({ kind: 'model', key: `model:${key}`, group });
    }
    return entries;
}

/** Un accesorio con modelo (no servicio ni kit) se puede juntar con sus variantes. */
export function isGroupableAccessory(item: CatalogItem): boolean {
    return item.category === 'ACCESSORY' && !isService(item) && item.model.trim() !== '';
}

/** Variante de accesorio: la capacidad tal cual viene («20 W», «1 m») y el color. */
export function accessoryVariantLabel(item: Pick<CatalogItem, 'capacity' | 'color'>): string {
    return [item.capacity.trim(), item.color.trim()].filter((part) => part !== '').join(' · ');
}

function compareAccessoryVariants(a: CatalogItem, b: CatalogItem): number {
    return (
        byText(a.capacity.trim(), b.capacity.trim()) ||
        byText(a.color.trim(), b.color.trim()) ||
        byText(a.sku, b.sku)
    );
}

/**
 * Accesorios de un mismo marca + modelo con dos o más SKU en un grupo (en la posición de su primer
 * SKU); lo demás, y el modelo de un solo SKU, por SKU. No cambia `catalogEntries`.
 */
export function accessoryCatalogEntries<T extends CatalogItem>(
    items: readonly T[],
): CatalogEntry<T>[] {
    const buckets = new Map<string, T[]>();
    for (const item of items.filter(isGroupableAccessory)) {
        const key = modelKeyOf(item);
        const bucket = buckets.get(key);
        if (bucket) bucket.push(item);
        else buckets.set(key, [item]);
    }
    const entries: CatalogEntry<T>[] = [];
    const placed = new Set<string>();
    for (const item of items) {
        const bucket = isGroupableAccessory(item) ? buckets.get(modelKeyOf(item)) : undefined;
        if (bucket === undefined || bucket.length < 2) {
            entries.push({ kind: 'item', key: item.sku, item });
            continue;
        }
        const key = modelKeyOf(item);
        if (placed.has(key)) continue;
        placed.add(key);
        entries.push({
            kind: 'model',
            key: `model:${key}`,
            group: buildGroup(key, bucket, compareAccessoryVariants),
        });
    }
    return entries;
}

/** El modelo y la variante de un SKU; `null` si no está en los grupos. */
export function findVariant<T extends CatalogItem>(
    groups: readonly ModelGroup<T>[],
    sku: string,
): { group: ModelGroup<T>; variant: T } | null {
    for (const group of groups) {
        const variant = group.variants.find((item) => item.sku === sku);
        if (variant) return { group, variant };
    }
    return null;
}

/** «6 variantes» / «1 variante». */
export function variantCountLabel(group: ModelGroup): string {
    const count = group.variants.length;
    return `${String(count)} ${count === 1 ? 'variante' : 'variantes'}`;
}

/** «$3,999 – $5,299»; un solo precio si todas las variantes cuestan lo mismo. */
export function priceRangeLabel(
    group: Pick<ModelGroup, 'minPriceCents' | 'maxPriceCents'>,
): string {
    const min = formatMXN(group.minPriceCents / 100);
    return group.minPriceCents === group.maxPriceCents
        ? min
        : `${min} – ${formatMXN(group.maxPriceCents / 100)}`;
}

/** «desde $3,999». */
export function fromPriceLabel(group: Pick<ModelGroup, 'minPriceCents'>): string {
    return `desde ${formatMXN(group.minPriceCents / 100)}`;
}

/** Fila de una tabla por modelo: el modelo, una de sus variantes desplegadas o un SKU suelto. */
export type ModelTableRow<T extends CatalogItem = CatalogItem> =
    | { kind: 'model'; key: string; group: ModelGroup<T>; expanded: boolean }
    | { kind: 'variant'; key: string; item: T; group: ModelGroup<T> }
    | { kind: 'item'; key: string; item: T };

/** Las entradas con las variantes de los modelos desplegados justo debajo de su modelo. */
export function modelTableRows<T extends CatalogItem>(
    entries: readonly CatalogEntry<T>[],
    expandedKeys: ReadonlySet<string>,
): ModelTableRow<T>[] {
    return entries.flatMap((entry): ModelTableRow<T>[] => {
        if (entry.kind === 'item') return [entry];
        const expanded = expandedKeys.has(entry.key);
        const head: ModelTableRow<T> = {
            kind: 'model',
            key: entry.key,
            group: entry.group,
            expanded,
        };
        if (!expanded) return [head];
        return [
            head,
            ...entry.group.variants.map(
                (item): ModelTableRow<T> => ({
                    kind: 'variant',
                    key: `${entry.key}:${item.sku}`,
                    item,
                    group: entry.group,
                }),
            ),
        ];
    });
}

/** La entrada que contiene un SKU, para desplegar su modelo al llegar por `?sku=`. */
export function entryKeyOfSku(entries: readonly CatalogEntry[], sku: string): string | null {
    const entry = entries.find((candidate) =>
        candidate.kind === 'item'
            ? candidate.item.sku === sku
            : candidate.group.variants.some((variant) => variant.sku === sku),
    );
    return entry?.key ?? null;
}

/** El SKU de la fila: la variante o el SKU suelto; un modelo no tiene uno solo. */
export function rowSku(row: ModelTableRow): string | null {
    return row.kind === 'model' ? null : row.item.sku;
}

/** «6 modelos · 23 variantes». */
export function modelsAndVariantsLabel(groups: readonly ModelGroup[]): string {
    const models = groups.length;
    const variants = groups.reduce((sum, group) => sum + group.variants.length, 0);
    return `${String(models)} ${models === 1 ? 'modelo' : 'modelos'} · ${String(variants)} ${variants === 1 ? 'variante' : 'variantes'}`;
}

/** Si algo (p. ej. una promo) aplica igual a todas las variantes, a algunas o a ninguna. */
export type VariantCoverage<T extends CatalogItem = CatalogItem> =
    | { kind: 'all'; sample: T }
    | { kind: 'some' }
    | { kind: 'none' };

/** Compara la firma de cada variante; la firma vacía es «no aplica». */
export function variantCoverage<T extends CatalogItem>(
    group: ModelGroup<T>,
    signatureOf: (item: T) => string,
): VariantCoverage<T> {
    const signatures = group.variants.map(signatureOf);
    if (signatures.every((signature) => signature === '')) return { kind: 'none' };
    const [first] = signatures;
    if (first !== '' && signatures.every((signature) => signature === first)) {
        return { kind: 'all', sample: group.variants[0] };
    }
    return { kind: 'some' };
}

/** Lo que dice la fila del modelo cuando la promo no aplica a todas sus variantes. */
export const SOME_VARIANTS_LABEL = 'En algunas variantes';

/** Una opción del selector de variante. `key` es el SKU al que lleva elegirla. */
export interface SelectorOption {
    key: string;
    label: string;
    selected: boolean;
    /** La combinación existe con lo ya elegido. */
    available: boolean;
    inStock: boolean;
}

export interface SelectorDimension {
    id: 'storage' | 'ram' | 'color';
    label: string;
    options: SelectorOption[];
}

/** Nombres de los ejes y cómo se lee la capacidad; sin ellos, los del teléfono. */
export interface VariantAxisLabels {
    storage?: string;
    ram?: string;
    color?: string;
    /** Texto de una capacidad; por defecto `formatStorage` («128 GB»). */
    formatCapacity?: (value: string | null | undefined) => string;
}

export interface VariantSelector {
    /** Ficha con las dimensiones de un solo valor: «8 GB RAM · 128 GB · Negro». */
    summary: string;
    /** Solo las dimensiones con dos o más opciones. */
    dimensions: SelectorDimension[];
}

const storageKeyOf = (item: CatalogItem): string => sizeKey(item.capacity);
const ramKeyOf = (item: CatalogItem): string => sizeKey(item.ram);
const colorOf = (item: CatalogItem): string => item.color.trim();

/** La que respeta más preferencias (pesan más las primeras), primero entre las que tienen stock. */
function pickVariant<T extends CatalogItem>(
    candidates: readonly T[],
    preferences: readonly ((item: T) => boolean)[],
): T | undefined {
    const score = (item: T): number =>
        preferences.reduce(
            (sum, prefers, index) => sum + (prefers(item) ? 2 ** (preferences.length - index) : 0),
            0,
        );
    const best = (pool: readonly T[]): T | undefined =>
        pool.reduce<T | undefined>(
            (top, item) => (top === undefined || score(item) > score(top) ? item : top),
            undefined,
        );
    return best(candidates.filter((item) => !isSoldOut(item))) ?? best(candidates);
}

/** Valores distintos de una dimensión, en el orden de las variantes (ya ordenadas por tamaño). */
function distinctValues<T extends CatalogItem>(
    variants: readonly T[],
    keyOf: (item: T) => string,
): T[] {
    const seen = new Set<string>();
    return variants.filter((item) => {
        const key = keyOf(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function option<T extends CatalogItem>(
    label: string,
    candidates: readonly T[],
    selected: boolean,
    preferences: readonly ((item: T) => boolean)[],
    fallbackKey: string,
): SelectorOption {
    const target = pickVariant(candidates, preferences);
    return {
        key: target?.sku ?? fallbackKey,
        label,
        selected,
        available: candidates.length > 0,
        inStock: candidates.some((item) => !isSoldOut(item)),
    };
}

/**
 * Selector almacenamiento → RAM → color de un modelo, con la variante elegida. Una combinación que
 * no existe queda no disponible; elegir una opción lleva al SKU más parecido con stock.
 */
export function variantSelector<T extends CatalogItem>(
    group: ModelGroup<T>,
    selected: T,
    labels: VariantAxisLabels = {},
): VariantSelector {
    const formatCapacity = labels.formatCapacity ?? formatStorage;
    const variants = group.variants;
    const storage = storageKeyOf(selected);
    const ram = ramKeyOf(selected);
    const color = colorOf(selected);
    const sameRam = (item: T) => ramKeyOf(item) === ram;
    const sameColor = (item: T) => colorOf(item) === color;
    const inStorage = variants.filter((item) => storageKeyOf(item) === storage);
    const inCombo = inStorage.filter(sameRam);

    const storageOptions = distinctValues(variants, storageKeyOf).map((sample) =>
        option(
            formatCapacity(sample.capacity) || 'Sin dato',
            variants.filter((item) => storageKeyOf(item) === storageKeyOf(sample)),
            storageKeyOf(sample) === storage,
            [sameRam, sameColor],
            `none:storage:${storageKeyOf(sample)}`,
        ),
    );

    const hasRam = variants.some((item) => ramKeyOf(item) !== '');
    const ramValues = [...distinctValues(variants, ramKeyOf)].sort((a, b) =>
        compareSize(a.ram, b.ram),
    );
    const ramOptions = ramValues.map((sample) =>
        option(
            formatStorage(sample.ram) || 'Sin dato',
            inStorage.filter((item) => ramKeyOf(item) === ramKeyOf(sample)),
            ramKeyOf(sample) === ram,
            [sameColor],
            `none:ram:${ramKeyOf(sample)}`,
        ),
    );

    const hasColor = variants.some((item) => colorOf(item) !== '');
    const colorValues = [...new Set(variants.map(colorOf))].sort(byText);
    const colorOptions = colorValues.flatMap((value): SelectorOption[] => {
        const candidates = inCombo.filter((item) => colorOf(item) === value);
        const label = value || 'Sin color';
        if (candidates.length <= 1) {
            return [option(label, candidates, value === color, [], `none:color:${value}`)];
        }
        // Por precio solo si todos son distintos; si alguno se repite, se numeran.
        const distinctPrices =
            new Set(candidates.map((item) => item.cashPriceCents)).size === candidates.length;
        return candidates.map((item, index) => ({
            key: item.sku,
            label: distinctPrices
                ? `${label} · ${formatMXN(item.cashPriceCents / 100)}`
                : `${label} · ${String(index + 1)}`,
            selected: item.sku === selected.sku,
            available: true,
            inStock: !isSoldOut(item),
        }));
    });

    const dimensions: SelectorDimension[] = [];
    const summary: string[] = [];
    if (hasRam) {
        if (ramOptions.length > 1)
            dimensions.push({ id: 'ram', label: labels.ram ?? 'RAM', options: ramOptions });
        else summary.push(ramLabel(selected.ram));
    }
    if (storageOptions.length > 1) {
        dimensions.unshift({
            id: 'storage',
            label: labels.storage ?? 'Almacenamiento',
            options: storageOptions,
        });
    } else summary.push(formatCapacity(selected.capacity));
    if (hasColor) {
        if (colorOptions.length > 1)
            dimensions.push({ id: 'color', label: labels.color ?? 'Color', options: colorOptions });
        else summary.push(color);
    }
    return { summary: summary.filter((part) => part !== '').join(' · '), dimensions };
}

/** Un SKU dentro de una celda de la matriz; `tag` numera los repetidos de mismo precio. */
export interface MatrixOption<T extends CatalogItem = CatalogItem> {
    item: T;
    tag: string | null;
    inStock: boolean;
}

export interface MatrixColumn {
    key: string;
    /** «8 GB RAM · 256 GB» o «128 GB». */
    label: string;
}

export interface MatrixRow<T extends CatalogItem = CatalogItem> {
    key: string;
    /** Nombre del color; vacío si el SKU no lo trae. */
    color: string;
    /** Una celda por columna; vacía si la combinación no existe. */
    cells: MatrixOption<T>[][];
}

export interface VariantMatrix<T extends CatalogItem = CatalogItem> {
    columns: MatrixColumn[];
    rows: MatrixRow<T>[];
}

const capacityKeyOf = (item: CatalogItem): string => `${ramKeyOf(item)}|${storageKeyOf(item)}`;

/** SKU repetidos en una celda: sin etiqueta si el precio los distingue; si no, se numeran. */
function cellOptions<T extends CatalogItem>(items: readonly T[]): MatrixOption<T>[] {
    const distinctPrices = new Set(items.map((item) => item.cashPriceCents)).size === items.length;
    return items.map((item, index) => ({
        item,
        tag: items.length > 1 && !distinctPrices ? String(index + 1) : null,
        inStock: !isSoldOut(item),
    }));
}

/** Matriz color (filas, alfabético) × capacidad con RAM (columnas); `columnLabel` nombra la columna. */
export function variantMatrix<T extends CatalogItem>(
    group: ModelGroup<T>,
    columnLabel: (sample: T) => string = variantCapacityLabel,
): VariantMatrix<T> {
    const columns = distinctValues(group.variants, capacityKeyOf).map((sample) => ({
        key: capacityKeyOf(sample),
        label: columnLabel(sample) || 'Sin dato',
    }));
    const colors = [...new Set(group.variants.map(colorOf))].sort(byText);
    const rows = colors.map((color) => ({
        key: color === '' ? 'none' : color,
        color,
        cells: columns.map((column) =>
            cellOptions(
                group.variants.filter(
                    (item) => colorOf(item) === color && capacityKeyOf(item) === column.key,
                ),
            ),
        ),
    }));
    return { columns, rows };
}
